import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import { SyncIcon } from '@sharedCR/coreDesignSystem/icons';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import { LOAN_ACTION_TRIGGER_EVENT } from 'app/pages/loanApplication/UniversalSearch/UniversalSearch.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';

import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';
import { getFormBundleForLoanByLoanIdAction } from 'store/forms/actions';
import { openDialogAction } from 'store/global/actions';
import {
  getDisclosureActivityAction,
  getLoanBundleHistoryAction,
  getUnderwritingDataAction,
} from 'store/loanApplications/actions';
import {
  selectDisclosureActivityEntries,
  selectDisclosureActivitySentByUserId,
  selectIsDisclosureActivityUnavailable,
  selectLoanActions,
  selectLoanBundleHistorySortedByDate,
} from 'store/loanApplications/selectors';
import { selectUnderwritingExpirationDates } from 'store/loanApplications/selectors/underwriting.selectors';
import { getUsersAction } from 'store/users/actions';
import { selectHasUsersLoaded } from 'store/users/selectors';

import DisclosurePackageItem from './components/DisclosurePackageItem';
import EcdChip from './components/EcdChip';
import ResendPackagePopup from './components/ResendPackagePopup';
import { TABLE_COLUMNS_NUMBER, tableConfig } from './DisclosureDelivery.config';
import {
  DisclosureDeliveryHost,
  getDisclosureDeliveryHostConfig,
} from './DisclosureDelivery.constants';
import {
  headerActionsStyles,
  liveDotStyles,
  liveIndicatorStyles,
  packageTableStyles,
} from './DisclosureDelivery.styles';
import { DisclosureDeliveryTestIds } from './DisclosureDelivery.testIds';
import { DisclosureDeliveryContext } from './DisclosureDeliveryContext';
import useDisclosureActorDirectory from './hooks/useDisclosureActorDirectory';
import useDisclosurePolling from './hooks/useDisclosurePolling';

const DisclosureDelivery = ({ host = DisclosureDeliveryHost.UNDERWRITING }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { applicationId } = useParams();

  const hostConfig = useMemo(() => getDisclosureDeliveryHostConfig(host), [host]);

  const [isLoading, setIsLoading] = useState(false);
  const [unavailableActions, setUnavailableActions] = useState(() => ({}));

  // Each affordance gates on the claim family its OWN endpoint enforces, so the panel never offers a
  // control the API will 403 (DEV-5614; formDelivery.* claim family per FH-1569):
  //   reads (View Documents / download / print / mail receipt)
  //     -> GET SkynetAPI /FormBundleSignatureRequest/..., or the same route on Skynet.Forms when
  //        FormsConsolidationEnabled is on (useSignatureRequestDocumentViewer)
  //        [FormBundleSignatureRequestPolicy.Access]
  //        = formBundleSignatureRequest.access, the family's ONLY claim (isManageAllowed accepts it).
  //   recipient remediation (reissue link / fix email / switch factor / mark as mailed)
  //     -> Skynet.Forms /api/FormDeliveryRequests/{id}/recipientRequests/... [FormDeliveryPolicy.Edit]
  //        = formDelivery.edit OR formDelivery.manage (isEditAllowed covers both).
  //   cancel a scheduled send
  //     -> DELETE SkynetAPI /loan/{loanId}/cancel-sending-bundle [LoanEditorPolicy.Edit]
  //        = { loanEditor.edit } ALONE — loanEditor.access IS granted to real roles but the server
  //        rejects it, so this one needs the strict (un-aliased) edit flag.
  //   correcting a generated-outside delivery's dates
  //     -> PUT SkynetAPI /FormBundleSignatureRequest/form-delivery/{id}/generated-outside-dates,
  //        or Skynet.Forms PUT /api/FormDeliveryRequests/{id} when FormsConsolidationEnabled is on
  //        = formDelivery.editGeneratedOutsideFormDelivery, a bare atomic claim (no page-family verb
  //        suffix), so it's read directly rather than through getMasterPermissionsToPage. Both lanes
  //        also end at the Skynet.Forms PUT, whose [FormDeliveryPolicy.Edit] additionally requires
  //        formDelivery.edit OR formDelivery.manage (SkynetAPI forwards the user's token to it).
  const { isManageAllowed: isReadAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.FORM_BUNDLE_SIGNATURE_REQUEST),
  );
  const { isEditAllowed: isFormMutationAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.FORM_DELIVERY),
  );
  const { isStrictEditAllowed: isCancelSendingAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_APPLICATION),
  );
  const isEditGeneratedOutsideDatesAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.FORM_DELIVERY_EDIT_GENERATED_OUTSIDE_DATES),
  );

  const loanActions = useSelector(selectLoanActions);
  const loanBundleHistorySorted = useSelector(selectLoanBundleHistorySortedByDate);
  const expirationDates = useSelector(selectUnderwritingExpirationDates);
  const auditEntries = useSelector(selectDisclosureActivityEntries);
  const isAuditUnavailable = useSelector(selectIsDisclosureActivityUnavailable);
  const sentByUserId = useSelector(selectDisclosureActivitySentByUserId);
  const hasUsersLoaded = useSelector(selectHasUsersLoaded);

  // "Issued by" names the person who issued/sent the disclosures. The actor is resolved against
  // employees + the loan's sub-company users, so a broker issuer resolves rather than showing the
  // tenant company or "Unknown user".
  const actorDirectory = useDisclosureActorDirectory({ enabled: hostConfig.showAuditStrip });
  const issuedBy = actorDirectory[sentByUserId]?.name ?? null;

  // The audit strip + trail resolve actor ids to names against the company user directory. Some
  // hosts (the standalone Documents subtab) don't otherwise load it, so populate it once when the
  // strip is shown and it's empty — guarded so we never clobber an already-loaded directory.
  useEffectSafeStrictMode(() => {
    if (hostConfig.showAuditStrip && !hasUsersLoaded) {
      dispatch(getUsersAction()).catch(_.noop);
    }
  }, [dispatch, hostConfig.showAuditStrip, hasUsersLoaded]);

  const fetchData = useCallback(
    ({ silent = false } = {}) => {
      if (!loanId) {
        return Promise.resolve();
      }

      if (!silent) {
        setIsLoading(true);
      }

      const requests = [
        dispatch(getLoanBundleHistoryAction({ loanId, applicationId, silent })),
        dispatch(getFormBundleForLoanByLoanIdAction({ loanId, silent })),
      ];

      // The disclosure activity trail (attestations/remediations/mailed-delivery) backs the audit
      // strip; only fetch it where the strip is shown, and never let its own rejection break the
      // finally (the thunk already degrades 404/403 internally).
      if (hostConfig.showAuditStrip) {
        requests.push(dispatch(getDisclosureActivityAction({ applicationId })));
      }

      // The ECD chip reads `selectUnderwritingExpirationDates`, which is only populated once
      // something has fetched the underwriting slice. The wizard's Step 1 (Readiness) fetches it
      // on open, so the chip "worked" there by coincidence — but landing straight on the
      // standalone Documents > Disclosures subtab (never opening the wizard, never visiting
      // Underwriting) leaves the slice empty and the chip silently absent. Reuse the exact same
      // thunk the wizard uses, gated to hosts that actually show the chip.
      if (hostConfig.showEcdChip) {
        requests.push(
          Promise.resolve(dispatch(getUnderwritingDataAction({ loanId, silent: true }))).catch(
            _.noop,
          ),
        );
      }

      return Promise.all(requests).finally(() => {
        if (!silent) {
          setIsLoading(false);
        }
      });
    },
    [dispatch, loanId, applicationId, hostConfig.showAuditStrip, hostConfig.showEcdChip],
  );

  // Poll must let rejections propagate so useDisclosurePolling can back off; the
  // hook owns the catch. Silent, so a transient outage never spams a toast.
  const pollData = useCallback(() => fetchData({ silent: true }), [fetchData]);

  // Foreground refreshes surface the shared error toast (silent: false) and
  // swallow the settled rejection so the click/effect has no dangling handler.
  const refreshData = useCallback(() => fetchData().catch(_.noop), [fetchData]);

  // Post-action refresh stays quiet but must not throw into a caller's await.
  const refreshSilently = useCallback(() => fetchData({ silent: true }).catch(_.noop), [fetchData]);

  const onManualRefresh = useCallback(() => {
    refreshData();
  }, [refreshData]);

  usePendingChangesDependentFetch(refreshData);

  useDisclosurePolling({ enabled: hostConfig.enablePolling, poll: pollData });

  const onRemediationUnavailable = useCallback(
    actionType =>
      setUnavailableActions(prev => (prev[actionType] ? prev : { ...prev, [actionType]: true })),
    [],
  );

  const sendFormBundleAction = useMemo(
    () =>
      (loanActions ?? []).find(action =>
        (action.loanActionTypes ?? []).some(
          type => type.type === LoanEditorActionsEnum.SEND_FORM_BUNDLE,
        ),
      ) ?? null,
    [loanActions],
  );

  const triggerSendFormBundles = useCallback(() => {
    if (!sendFormBundleAction) {
      return;
    }

    document.dispatchEvent(
      new CustomEvent(LOAN_ACTION_TRIGGER_EVENT, {
        detail: { actionId: sendFormBundleAction.id, actionName: sendFormBundleAction.name },
      }),
    );
  }, [sendFormBundleAction]);

  const onResendPackage = useCallback(
    item => {
      dispatch(
        openDialogAction({
          header: 'Resend package',
          body: () => (
            <ResendPackagePopup
              bundleName={item.formBundleName}
              onConfirm={triggerSendFormBundles}
            />
          ),
        }),
      );
    },
    [dispatch, triggerSendFormBundles],
  );

  // Resend re-triggers the loan's own SendFormBundles action through the loan-action framework
  // (POST /api/manual-actions/execute, authenticated but claim-free) — the action's presence in the
  // loan's action list IS the server's gate, exactly as the loan header applies it. Layering a
  // claim check on top would hide a button from users the API happily serves.
  const canResendPackage = hostConfig.enablePackageResend && !!sendFormBundleAction;

  const contextValue = useMemo(
    () => ({
      host,
      hostConfig,
      loanId,
      applicationId,
      isReadAllowed,
      isFormMutationAllowed,
      isCancelSendingAllowed,
      isEditGeneratedOutsideDatesAllowed,
      issuedBy,
      actorDirectory,
      auditEntries,
      isAuditUnavailable,
      unavailableActions,
      onRemediationUnavailable,
      refresh: refreshSilently,
    }),
    [
      host,
      hostConfig,
      loanId,
      applicationId,
      isReadAllowed,
      isFormMutationAllowed,
      isCancelSendingAllowed,
      isEditGeneratedOutsideDatesAllowed,
      issuedBy,
      actorDirectory,
      auditEntries,
      isAuditUnavailable,
      unavailableActions,
      onRemediationUnavailable,
      refreshSilently,
    ],
  );

  const data = useMemo(() => {
    if (!loanBundleHistorySorted) {
      return [];
    }

    return loanBundleHistorySorted.map(item => ({
      formBundleName: {
        position: 1,
        colSpan: TABLE_COLUMNS_NUMBER,
        content: (
          <DisclosurePackageItem
            canResend={canResendPackage}
            item={item}
            onResendPackage={onResendPackage}
          />
        ),
      },
    }));
  }, [loanBundleHistorySorted, canResendPackage, onResendPackage]);

  const titleNode = hostConfig.showSectionSubtitle ? (
    <Box>
      <Typography variant="medium20">{hostConfig.sectionTitle}</Typography>
      {hostConfig.enablePolling && (
        <Box sx={liveIndicatorStyles}>
          <Box sx={liveDotStyles} />
          <Typography color="text.secondary" variant="regular12">
            Updates in real time
          </Typography>
        </Box>
      )}
    </Box>
  ) : (
    hostConfig.sectionTitle
  );

  const actionContent =
    hostConfig.showEcdChip || hostConfig.showManualRefresh ? (
      <Box sx={headerActionsStyles}>
        {hostConfig.showEcdChip && (
          <EcdChip earliestClosingDate={expirationDates?.earliestClosingDate} />
        )}
        {hostConfig.showManualRefresh && (
          <Button
            data-testid={DisclosureDeliveryTestIds.REFRESH_BUTTON}
            disabled={isLoading}
            size="small"
            startIcon={<SyncIcon />}
            variant="outlined"
            onClick={onManualRefresh}
          >
            Refresh
          </Button>
        )}
      </Box>
    ) : null;

  return (
    <DisclosureDeliveryContext.Provider value={contextValue}>
      <PageBlock
        withDivider
        actionContent={actionContent}
        title={titleNode}
        withAction={!!actionContent}
      >
        <TableCDS
          isCustomDataDefinition
          withNoAvailableMessage
          config={tableConfig}
          customStyles={packageTableStyles}
          data={data}
          data-testid={DisclosureDeliveryTestIds.ROOT}
          isLoading={isLoading}
          withDivider={false}
        />
      </PageBlock>
    </DisclosureDeliveryContext.Provider>
  );
};

DisclosureDelivery.propTypes = {
  host: PropTypes.oneOf(Object.values(DisclosureDeliveryHost)),
};

export default memo(DisclosureDelivery);
